import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import formatPhone from '../utils/formatPhone';
import { getUnoModulo, getUnoInversorBomb, getIndices, getEmpresaAtual } from './api';
import {
  coresHex, hexParaRgb,
} from '../styles/styleUtils';

// Converte um hex de coresHex em rgb() 0-1 do pdf-lib (que não entende var()/hex).
const rgbHex = (hex) => {
  const { r, g, b } = hexParaRgb(hex);
  return rgb(r / 255, g / 255, b / 255);
};

// ============================================================
// Proposta Comercial — Sistema de Bombeamento — Marcki v2
// ============================================================

const generatePdfBombeamento = async (proposta) => {
  if (!proposta) return;

  const pdfDoc  = await PDFDocument.create();
  const fontReg = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold= await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const W = 595.28; const H = 841.89;
  const ML = 40;    const MR = 40;
  const CW = W - ML - MR;

  const C = {
    azul:      rgb(0.102, 0.227, 0.431),
    azulClaro: rgb(0.910, 0.941, 0.969),
    laranja:   rgbHex(coresHex.laranja),
    verde:     rgb(0.180, 0.490, 0.196),
    cinza:     rgb(0.412, 0.412, 0.412),
    cinzaClaro:  rgb(0.941, 0.941, 0.941),
    cinzaChumbo: rgb(0.800, 0.800, 0.800),
    bordaAzul: rgb(0.690, 0.769, 0.847),
    preto:     rgb(0.133, 0.133, 0.133),
    branco:    rgb(1, 1, 1),
  };

  // ── Helpers ────────────────────────────────────────────────
  const cx  = (t, s, f) => (W - f.widthOfTextAtSize(t, s)) / 2;
  const brl = (v) => parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const drawRect = (pg, x, y, w, h, color) => pg.drawRectangle({ x, y, width: w, height: h, color });
  const drawBRect= (pg, x, y, w, h, fill, border) => pg.drawRectangle({ x, y, width: w, height: h, color: fill, borderColor: border || C.bordaAzul, borderWidth: 0.5 });
  const drawText = (pg, t, x, y, s, f, color) => pg.drawText(String(t ?? ''), { x, y, size: s, font: f, color });
  const drawLine = (pg, x1, y1, x2, y2, color = C.bordaAzul, t = 0.5) => pg.drawLine({ start:{x:x1,y:y1}, end:{x:x2,y:y2}, thickness:t, color });

  // Quebra texto automaticamente pela largura máxima em pontos
  const wrapText = (text, maxW, size, font) => {
    const words = text.split(' ');
    const lines = [];
    let cur = '';
    words.forEach(w => {
      const test = cur ? `${cur} ${w}` : w;
      if (font.widthOfTextAtSize(test, size) <= maxW) {
        cur = test;
      } else {
        if (cur) lines.push(cur);
        cur = w;
      }
    });
    if (cur) lines.push(cur);
    return lines;
  };

  // Desenha parágrafo com wrap automático, retorna y final
  const drawParagraph = (pg, text, x, y, maxW, size, font, color, lineH) => {
    const lines = wrapText(text, maxW, size, font);
    lines.forEach(l => { drawText(pg, l, x, y, size, font, color); y -= lineH; });
    return y;
  };

  const tH = 14;
  const drawRow = (pg, cells, y) => {
    cells.forEach((c) => {
      drawBRect(pg, c.x, y - tH + 2, c.w, tH, c.fill, c.bc);
      if (c.text != null && c.text !== '') {
        const sz = c.sz || 8; const fn = c.fn || fontReg;
        let tx = c.x + 3;
        if (c.al === 'r') tx = c.x + c.w - fn.widthOfTextAtSize(String(c.text), sz) - 3;
        if (c.al === 'c') tx = c.x + (c.w - fn.widthOfTextAtSize(String(c.text), sz)) / 2;
        drawText(pg, c.text, tx, y - tH + 5, sz, fn, c.cl || C.preto);
      }
    });
    return y - tH;
  };

  // Desenha gráfico de barras com eixos, rótulos no topo e eixo X com labels
  // Retorna o y logo abaixo do gráfico (após legenda/nota)
  const drawBarChart = (pg, data, y, availH, opts = {}) => {
    const {
      colors = [C.laranja],      // cor por série
      labelKey = 'label',        // chave do label do eixo X
      valueKeys = ['v'],         // chaves dos valores (uma por série)
      legends = [],              // legendas das séries
      noteTxt = '',              // nota de rodapé
      ySteps = 5,                // número de divisões no eixo Y
    } = opts;

    const nBars   = data.length;
    const nSeries = valueKeys.length;
    const gW      = CW / nBars;
    const bW      = (gW / (nSeries + 1)) * 0.9;
    const axisH   = 16;  // altura reservada para labels eixo X
    const legH    = legends.length > 0 ? 18 : 0;
    const noteH   = noteTxt ? 22 : 0;
    const barMaxH = availH - axisH - legH - noteH - 24; // espaço para eixo Y labels topo

    // Máximo global
    const allVals = data.flatMap(d => valueKeys.map(k => d[k] || 0));
    const maxVal  = Math.max(...allVals, 0.01);
    const baseY   = y - availH + noteH + legH + axisH;

    // Eixo Y — linha vertical + valores
    const yStepVal = maxVal / ySteps;
    for (let s = 0; s <= ySteps; s += 1) {
      const yy = baseY + (s / ySteps) * barMaxH;
      // Linha de grade horizontal tracejada
      drawLine(pg, ML + 18, yy, ML + CW, yy, C.cinzaClaro, 0.4);
      // Valor no eixo Y
      const label = yStepVal >= 100
        ? Math.round(s * yStepVal).toString()
        : (s * yStepVal).toFixed(1);
      drawText(pg, label, ML, yy - 3, 5.5, fontReg, C.cinza);
    }
    // Linha do eixo Y
    drawLine(pg, ML + 18, baseY, ML + 18, baseY + barMaxH + 4, C.cinza, 0.8);
    // Linha do eixo X
    drawLine(pg, ML + 18, baseY, ML + CW, baseY, C.cinza, 0.8);

    // Barras
    data.forEach((d, i) => {
      const xGroup = ML + 18 + i * (CW - 18) / nBars;
      valueKeys.forEach((k, si) => {
        const val = d[k] || 0;
        const h   = (val / maxVal) * barMaxH;
        const x   = xGroup + si * (bW + 2) + ((CW - 18) / nBars - nSeries * bW - (nSeries - 1) * 2) / 2;
        drawRect(pg, x, baseY, bW, h, colors[si] || C.laranja);
        // Rótulo no topo
        const rot  = val >= 100 ? Math.round(val).toString() : val.toFixed(1);
        const rotW = fontBold.widthOfTextAtSize(rot, 6.5);
        drawText(pg, rot, x + (bW - rotW) / 2, baseY + h + 3, 6.5, fontBold, C.preto);
      });
      // Label eixo X
      const lbl  = d[labelKey];
      const lblW = fontBold.widthOfTextAtSize(lbl, 7);
      drawText(pg, lbl, xGroup + ((CW - 18) / nBars - lblW) / 2, baseY - 11, 7, fontBold, C.cinza);
    });

    // Legenda
    if (legends.length > 0) {
      let lx = ML + 18;
      legends.forEach((leg, li) => {
        drawRect(pg, lx, baseY - axisH - legH + 4, 9, 9, colors[li] || C.laranja);
        drawText(pg, leg, lx + 12, baseY - axisH - legH + 5, 7.5, fontReg, C.preto);
        lx += 9 + 12 + fontReg.widthOfTextAtSize(leg, 7.5) + 16;
      });
    }

    // Nota
    if (noteTxt) {
      const ny = y - availH + noteH - 4;
      drawParagraph(pg, noteTxt, ML, ny, CW, 6.5, fontReg, C.cinza, 9);
    }

    return y - availH;
  };

  const drawSecHeader = (pg, label, y, bg = C.laranja) => {
    drawRect(pg, ML, y - 4, CW, 18, bg);
    drawText(pg, label, ML + 6, y, 10, fontBold, C.branco);
    return y - 24;
  };

  // ── Imagens estáticas ─────────────────────────────────────
  const fetchImg = async (path) => {
    try {
      const b = await fetch(path).then(r => r.arrayBuffer());
      if (path.endsWith('.png') || path.endsWith('.PNG')) return await pdfDoc.embedPng(b);
      return await pdfDoc.embedJpg(b);
    } catch(e) {
      console.warn(`Falha ao incorporar imagem "${path}" no PDF:`, e);
      return null;
    }
  };

  const imgBombeamento = await fetchImg('/Imagem Proposta Bombeamento.png');
  const estruturaImg   = await fetchImg('/produtos/Estrutura Solo.png');

  const b64ToImg = async (base64Str) => {
    try {
      const part = base64Str.split(',')[1];
      const bin  = atob(part);
      const arr  = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
      if (base64Str.includes('image/png')) return await pdfDoc.embedPng(arr);
      return await pdfDoc.embedJpg(arr);
    } catch(e) {
      console.warn('Falha ao incorporar logo da empresa no PDF:', e);
      return null;
    }
  };

  // Logo da empresa configurada — antes fixa em /logo.png / /logoweg.png
  const empresa    = await getEmpresaAtual();
  const logoImg    = empresa?.logoPrincipal ? await b64ToImg(empresa.logoPrincipal) : null;
  const logoWegImg = empresa?.logoParceiro  ? await b64ToImg(empresa.logoParceiro)  : null;

  let moduloImg = null;
  try {
    if (proposta.kit?.modulo) {
      const mod = await getUnoModulo(proposta.kit.modulo);
      if (mod?.foto) moduloImg = await b64ToImg(mod.foto);
    }
  } catch(e) { /* sem foto */ }

  let inversorImg = null;
  try {
    if (proposta.kit?.inversor) {
      const inv = await getUnoInversorBomb(proposta.kit.inversor);
      if (inv?.foto) inversorImg = await b64ToImg(inv.foto);
    }
  } catch(e) { /* sem foto */ }
  if (!inversorImg) inversorImg = await fetchImg('/produtos/inversor.png');

  // ── Índices ───────────────────────────────────────────────
  const indices      = await getIndices().catch(() => []);
  const tarifaAtual  = proposta.dadosBombeamento?.tarifaEnergia
    ? parseFloat(proposta.dadosBombeamento.tarifaEnergia)
    : (indices.find(i => i.nome === 'Tarifa CEMIG Atual')?.valor || 0.99);
  const dieselAtual  = indices.find(i => i.nome === 'Valor Diesel Atual')?.valor || 6.69;

  const anos  = [2016,2017,2018,2019,2020,2021,2022,2023,2024,2025];
  const hTar  = anos.map(a => indices.find(i => i.nome === `Tarifa CEMIG ${a}`)?.valor || 0);
  const hDie  = anos.map(a => indices.find(i => i.nome === `Valor Diesel ${a}`)?.valor || 0);

  // ── Dados do dimensionamento ──────────────────────────────
  const dados = proposta.dadosBombeamento || {};
  const dim   = proposta.dimensionamentoBomb || {};
  const meses = dim.meses || [];
  const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

  const potCC    = dim.potCC || 0;
  const potKit   = proposta.kit?.potenciacc || 0;
  const consumo  = dim.consumoMensal || 0;
  const hspMin   = dim.hspMin || 0;
  const rendBomba= dim.rendBomba || 0.4;
  const qtdMod   = proposta.kit?.quantidademodulos || 0;
  const area     = (qtdMod * 2.75).toFixed(1);
  const pesoTot  = ((proposta.kit?.modulo?.peso || 31.7) * qtdMod).toFixed(1);

  const vazMedia = meses.length > 0
    ? (meses.reduce((s,m) => s + (m.vazaoHoraria||0), 0) / 12) : 0;
  const ecoAnual = meses.reduce((s,m) => s + (m.economiaMes||0), 0);
  const ecoMedia = meses.length > 0 ? ecoAnual / 12 : 0;

  const anoAtual = new Date().getFullYear();
  const analise5 = Array.from({length:5}, (_,i) => ({
    ano:     anoAtual + i,
    consumo: consumo * 12,
    energia: consumo * 12 * tarifaAtual * (1.05 ** i),
    diesel:  consumo * 12 * (dieselAtual / 10) * (1.05 ** i),
  }));

  const valorProjeto = parseFloat(proposta.valor) || 0;
  const calcParcela  = (v, n) => {
    const t=0.0185; const iof=999; const cad=v*0.034;
    return (v+iof+cad)*(t*((1+t)**n))/(((1+t)**n)-1);
  };
  const payback = ecoMedia > 0 ? (valorProjeto / (ecoMedia * 12)).toFixed(2) : '–';

  // ============================================================
  // PG 1 — CAPA
  // ============================================================
  const p1 = pdfDoc.addPage([W, H]);

  if (logoImg) {
    const d = logoImg.scale(0.22);
    p1.drawImage(logoImg, { x:(W-d.width)/2, y:H-d.height-28, width:d.width, height:d.height });
  }
  if (logoWegImg) {
    const d = logoWegImg.scale(0.5);
    p1.drawImage(logoWegImg, { x:(W-d.width)/2, y:H-170-d.height, width:d.width, height:d.height });
  }

  ['Proposta Comercial','Sistema Bombeamento'].forEach((t, i) => {
    drawText(p1, t, cx(t, 16, fontBold), H - 320 - i*22, 16, fontBold, C.azul);
  });

  const lX=ML+30; const vX=lX+130; let dy=H-390;
  const dd = (l, v) => { drawText(p1,l,lX,dy,10,fontBold,C.azul); drawText(p1,v||'—',vX,dy,10,fontReg,C.preto); dy-=22; };
  dd('Cliente:',   proposta.cliente?.nome);
  dd('Telefone:',  formatPhone(proposta.cliente?.telefone||''));
  dd('Data:',      new Date().toLocaleDateString('pt-BR'));
  dd('Consultor:', proposta.user?.nome);
  dd('Contato:',   formatPhone(proposta.user?.telefone||''));
  dy -= 11;
  dd('Nosso número:', empresa?.telefone || '(34) 99925-5093');
  dd('Pós-vendas:',   empresa?.telefone || '(34) 98887-5093');

  const nomeCapa = empresa?.nome || 'MARCKI Engenharia';
  const enderecoCapa = empresa?.enderecoRodape || 'Araguari/MG';
  drawText(p1, nomeCapa,     cx(nomeCapa, 11, fontBold), 70, 11, fontBold, C.azul);
  drawText(p1, enderecoCapa, cx(enderecoCapa, 10, fontReg), 55, 10, fontReg, C.azul);

  // ============================================================
  // PG 2 — COMO FUNCIONA
  // ============================================================
  const p2 = pdfDoc.addPage([W, H]);
  let y2 = H - 30;

  y2 = drawSecHeader(p2, '1 | Como funciona o sistema de bombeamento fotovoltaico?', y2);
  y2 -= 6;

  // Imagem do sistema primeiro
  if (imgBombeamento) {
    const imgH = CW * (1024/1536);
    p2.drawImage(imgBombeamento, { x:ML, y:y2-imgH, width:CW, height:imgH });
    y2 -= imgH + 10;
  }

  // Caixa cinza com texto oficial Marcki abaixo da imagem
  const blocoTexto = [
    { t: 'O sistema utiliza a energia gerada pelos módulos fotovoltaicos para alimentar o inversor de frequência, que controla', sp: 11 },
    { t: 'automaticamente o funcionamento da motobomba de acordo com a intensidade da radiação solar ao longo do dia.', sp: 11 },
    { t: 'Podendo ser bombeado diretamente para sua lavoura ou para um reservatório para uso posterior.', sp: 16 },
    { t: 'Dessa forma, a vazão é ajustada de maneira 100% autônoma, aproveitando ao máximo a energia disponível para', sp: 11 },
    { t: 'bombear água com eficiência e segurança.', sp: 16 },
    { t: 'Além de controlar a operação da bomba, o inversor de frequência também incorpora sistemas de proteção contra:', sp: 16 },
    { t: '- Sobrecarga;', sp: 11 },
    { t: '- Curto-circuito;', sp: 11 },
    { t: '- Subtensão;', sp: 11 },
    { t: '- Sobretensão;', sp: 11 },
    { t: '- Operação a seco.', sp: 16 },
    { t: 'Isso proporciona maior vida útil ao equipamento, reduz custos com manutenção e garante mais confiabilidade para a', sp: 11 },
    { t: 'operação da propriedade.', sp: 16 },
    { t: 'Enquanto o sistema trabalha automaticamente utilizando a energia do sol, você pode focar no que realmente', sp: 11 },
    { t: 'importa: aumentar a produtividade da sua propriedade com mais segurança hídrica, economia e autonomia.', sp: 11 },
  ];
  const totalLinhas = blocoTexto.reduce((s, l) => s + l.sp, 0);
  const caixaH = totalLinhas + 20;
  drawRect(p2, ML, y2 - caixaH, CW, caixaH, C.cinzaClaro);
  drawBRect(p2, ML, y2 - caixaH, CW, caixaH, C.cinzaClaro, C.bordaAzul);
  let ytxt = y2 - 10;
  blocoTexto.forEach(l => {
    drawText(p2, l.t, ML+8, ytxt, 8, fontBold, C.preto);
    ytxt -= l.sp;
  });
  y2 -= caixaH + 8;

  // Benefícios
  y2 = drawSecHeader(p2, 'Benefícios do Sistema', y2, C.laranja);
  y2 -= 4;
  [
    ['Autonomia',        'Independência da rede elétrica e do combustível.'],
    ['Economia',         'Redução significativa nos custos operacionais.'],
    ['Disponibilidade',  'Água disponível todos os dias, mesmo em áreas remotas.'],
    ['Sustentabilidade', 'Energia limpa que preserva o meio ambiente.'],
    ['Tecnologia WEG',   'Equipamentos robustos, eficientes e de alta durabilidade.'],
  ].forEach(([t, d]) => {
    const tw = fontBold.widthOfTextAtSize(`• ${t}: `, 8);
    drawText(p2, `• ${t}: `, ML, y2, 8, fontBold, C.laranja);
    drawText(p2, d, ML + tw + 2, y2, 8, fontReg, C.preto);
    y2 -= 12;
  });

  // ============================================================
  // PG 3 — ANALISANDO AS MOTOBOMBAS
  // ============================================================
  const p3 = pdfDoc.addPage([W, H]);
  let y3 = H - 30;

  y3 = drawSecHeader(p3, '2 | Analisando as motobombas', y3);
  y3 -= 6;

  // Texto introdutório — wrap automático
  y3 = drawParagraph(p3, 'Nossa empresa entende a importância de avaliar cada cenário de forma particular e personalizada, pois no campo, as particularidades de cada lavoura, cada local, metodologia de manejo são a identidade de cada produtor e devem ser seguidas à risca. A seguir, apresentamos um resumo das informações que utilizaremos para o dimensionamento do seu sistema.', ML, y3, CW, 8, fontBold, C.preto, 11);
  y3 -= 8;

  // Tabela dados da bomba — cinza chumbo
  const cA = CW*0.55; const cB = CW*0.30; const cC = CW*0.15;
  [
    ['Motobomba já existente:', dados.possuiBomba==='sim'?'Sim':'Não', ''],
    ['Potência do motor:',      `${dados.potenciaMotorCV||'–'}`,       'CV'],
    ['Tensão de Operação:',     `${dados.tensaoOperacao||'–'}`,        'V'],
    ['Altura Manométrica:',     `${dados.alturaManometrica||'–'}`,     'm'],
    ['Dias de Operação:',       `${dados.diasFuncionamento||'–'}`,     'dias'],
    ['Horas de Operação:',      `${dados.horasFuncionamento||'–'}`,    'h'],
    ['Vazão Original:',         `${dados.vazaoAtual||'–'}`,            'm³/h'],
  ].forEach(([l, v, u]) => {
    y3 = drawRow(p3, [
      {x:ML,       w:cA, fill:C.cinzaChumbo, text:l, fn:fontBold, cl:C.cinza,  bc:C.preto},
      {x:ML+cA,    w:cB, fill:C.branco, text:v, fn:fontBold, cl:C.preto, bc:C.preto, al:'r'},
      {x:ML+cA+cB, w:cC, fill:C.branco, text:u, fn:fontReg,  cl:C.cinza, bc:C.preto},
    ], y3);
  });
  y3 -= 6;

  // Cidade + Tarifa — cinza chumbo
  const c1=CW*0.28; const c2=CW*0.22; const c3=CW*0.25; const c4=CW*0.25;
  y3 = drawRow(p3, [
    {x:ML,          w:c1, fill:C.cinzaChumbo, text:'Cidade de Instalação:', fn:fontBold, cl:C.cinza,  bc:C.preto},
    {x:ML+c1,       w:c2, fill:C.branco,      text:proposta.localinstalacao?.cidade?.nome||'', fn:fontBold, cl:C.preto, bc:C.preto},
    {x:ML+c1+c2,    w:c3, fill:C.cinzaChumbo, text:'Estado:',               fn:fontBold, cl:C.cinza,  bc:C.preto},
    {x:ML+c1+c2+c3, w:c4, fill:C.branco,      text:proposta.localinstalacao?.estado?.sigla||'', fn:fontBold, cl:C.preto, bc:C.preto},
  ], y3);
  y3 = drawRow(p3, [
    {x:ML,          w:c1, fill:C.cinzaChumbo, text:'Valor do kWh:',                    fn:fontBold, cl:C.cinza,  bc:C.preto},
    {x:ML+c1,       w:c2, fill:C.branco,      text:`R$ ${tarifaAtual.toFixed(2)}`,     fn:fontBold, cl:C.preto,  bc:C.preto},
    {x:ML+c1+c2,    w:c3, fill:C.cinzaChumbo, text:'Valor do Diesel:',                 fn:fontBold, cl:C.cinza,  bc:C.preto},
    {x:ML+c1+c2+c3, w:c4, fill:C.branco,      text:`R$ ${dieselAtual.toFixed(2)}`,     fn:fontBold, cl:C.preto,  bc:C.preto},
  ], y3);
  y3 -= 8;

  // Tabela 5 anos — cabeçalho laranja
  drawRect(p3, ML, y3-24, CW, 26, C.laranja);
  drawText(p3, 'Uma pergunta importante? Você já parou qual seria o consumo deste motor e o quanto', ML+4, y3-8, 7, fontBold, C.branco);
  drawText(p3, 'seria pago de energia elétrica ou Diesel durante os 5 primeiros anos de uso? Vamos analisar!', ML+4, y3-18, 7, fontBold, C.branco);
  y3 -= 26;

  const a1=CW*0.12; const a2=CW*0.20; const a3=CW*0.12; const a4=CW*0.28; const a5=CW*0.28;
  y3 = drawRow(p3, [
    {x:ML,             w:a1, fill:C.cinzaChumbo, text:'Ano',              fn:fontBold, cl:C.preto, al:'c'},
    {x:ML+a1,          w:a2, fill:C.cinzaChumbo, text:'Consumo Anual',    fn:fontBold, cl:C.preto, al:'c'},
    {x:ML+a1+a2,       w:a3, fill:C.cinzaChumbo, text:'Reajuste anual',   fn:fontBold, cl:C.preto, al:'c'},
    {x:ML+a1+a2+a3,    w:a4, fill:C.cinzaChumbo, text:'Energia Elétrica', fn:fontBold, cl:C.preto, al:'c'},
    {x:ML+a1+a2+a3+a4, w:a5, fill:C.cinzaChumbo, text:'Valor pago Diesel',fn:fontBold, cl:C.preto, al:'c'},
  ], y3);
  analise5.forEach((r, i) => {
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    y3 = drawRow(p3, [
      {x:ML,             w:a1, fill, text:r.ano,                     fn:fontBold, cl:C.preto,   al:'c'},
      {x:ML+a1,          w:a2, fill, text:`${Math.round(r.consumo)} kWh`, al:'c'},
      {x:ML+a1+a2,       w:a3, fill, text:'5%',                      al:'c'},
      {x:ML+a1+a2+a3,    w:a4, fill, text:brl(r.energia),            fn:fontBold, cl:C.laranja, al:'r'},
      {x:ML+a1+a2+a3+a4, w:a5, fill, text:brl(r.diesel),             fn:fontBold, cl:C.laranja, al:'r'},
    ], y3);
  });
  y3 -= 10;

  // Título "Uma conta que não para de subir..."
  drawText(p3, 'Uma conta que não para de subir...', cx('Uma conta que não para de subir...', 9, fontBold), y3, 9, fontBold, C.preto);
  y3 -= 12;

  // Texto explicativo — wrap automático
  y3 = drawParagraph(p3, 'Além do aumento natural da tarifa de energia ou do diesel, que acompanham a inflação e demais oscilações de mercado, sempre existe a tendência do aumento da área irrigada ou do aumento das horas de irrigação, em função das variações climáticas existentes. A seguir, apresentamos dois estudos sendo um da variação da tarifa de energia elétrica e outro do valor do diesel, com o intuito de ilustrar que, a dependência de fontes externas em muitas vezes não trás previsibilidade para você, produtor.', ML, y3, CW, 7.5, fontReg, C.preto, 10);
  y3 -= 4;

  // Título do gráfico
  drawText(p3, 'Variações Diesel e Energia Elétrica',
    cx('Variações Diesel e Energia Elétrica', 9, fontBold), y3, 9, fontBold, C.preto);
  y3 -= 14;

  // Gráfico ocupa todo o espaço restante da página
  const gLegH = 30;   // altura reservada para legenda + anos
  const gRotH = 16;   // altura reservada para rótulos acima das barras
  const gH    = y3 - 40 - gLegH - gRotH; // altura disponível para as barras
  const gW    = CW / anos.length;
  const bW    = gW * 0.36;
  const maxD  = Math.max(...hDie, 0.01);
  const maxT  = Math.max(...hTar, 0.01);
  const baseY = 40 + gLegH; // base das barras

  // Eixo Y do gráfico histórico
  const ySteps3 = 5;
  const yMax3   = Math.max(maxD, maxT);
  for (let s = 0; s <= ySteps3; s += 1) {
    const yy = baseY + (s / ySteps3) * gH;
    drawLine(p3, ML + 18, yy, ML + CW, yy, C.cinzaClaro, 0.4);
    drawText(p3, `R$${((s/ySteps3)*yMax3).toFixed(2)}`, ML, yy - 3, 5, fontReg, C.cinza);
  }
  drawLine(p3, ML + 18, baseY, ML + 18, baseY + gH + 4, C.cinza, 0.8);
  drawLine(p3, ML + 18, baseY, ML + CW, baseY, C.cinza, 0.8);

  anos.forEach((ano, i) => {
    const x  = ML + 18 + i * (CW - 18) / anos.length;
    const bw2 = (CW - 18) / anos.length;
    const hd = hDie[i] > 0 ? (hDie[i]/maxD) * gH         : 0;
    const ht = hTar[i] > 0 ? (hTar[i]/maxT) * (gH * 0.7) : 0;
    drawRect(p3, x+2,      baseY, bW, hd, C.cinzaChumbo);
    drawRect(p3, x+2+bW+2, baseY, bW, ht, C.laranja);
    if (hDie[i]>0) drawText(p3, `R$${hDie[i].toFixed(2)}`, x+1,      baseY+hd+3, 6.5, fontBold, C.preto);
    if (hTar[i]>0) drawText(p3, `R$${hTar[i].toFixed(2)}`, x+2+bW+1, baseY+ht+3, 6.5, fontBold, C.laranja);
    const aW = fontBold.widthOfTextAtSize(String(ano), 7);
    drawText(p3, String(ano), x + (bw2 - aW)/2, baseY-13, 7, fontBold, C.cinza);
  });

  // Legenda
  const legY = baseY - 26;
  drawRect(p3, ML+18,     legY, 9, 9, C.cinzaChumbo); drawText(p3,'Tarifa Diesel', ML+32,  legY+1, 8, fontReg, C.preto);
  drawRect(p3, ML+140,    legY, 9, 9, C.laranja);     drawText(p3,'Tarifa CEMIG',  ML+154, legY+1, 8, fontReg, C.preto);

  // ============================================================
  // PG 4 — SOLUÇÃO PROPOSTA
  // ============================================================
  const p4 = pdfDoc.addPage([W, H]);
  let y4 = H - 30;

  y4 = drawSecHeader(p4, '2 | Solução Proposta', y4);
  y4 -= 6;

  y4 = drawParagraph(p4, 'De posse das informações referentes à operação da motobomba, apresentamos o sistema fotovoltaico proposto para atendimento da demanda hídrica, com as respectivas vazões médias e volume de água bombeado, considerando o período de operação durante a produção solar.', ML, y4, CW, 8, fontReg, C.preto, 11);
  y4 -= 8;

  // Resumo do sistema — padrão cinzaChumbo + branco + bordas pretas
  y4 = drawSecHeader(p4, 'Resumo do sistema:', y4, C.laranja);
  y4 -= 4;

  const rL = CW*0.55; const rV = CW*0.30; const rU = CW*0.15;
  [
    ['Quantidade de módulos:', `${qtdMod}`,                          ''],
    ['Potência Total (kWp):',  `${potKit}`,                         'kWp'],
    ['Área Ocupada (m²):',     `${area}`,                           'm²'],
    ['Potência CC:',           `${potCC}`,                          'kW'],
    ['HSP Mínimo:',            `${hspMin}`,                         'h/dia'],
    ['Rend. Bomba:',           `${((rendBomba)*100).toFixed(0)}%`,  ''],
    ['Nova Vazão (m³/h):',     `${vazMedia.toFixed(2)}`,            'm³/h'],
    ['Volume Bombeado Diário:',`${(meses.length>0?meses.reduce((s,m)=>s+(m.vazaoDiaria||0),0)/12:0).toFixed(2)}`, 'm³/dia'],
    ['Média Mensal:',          `${consumo}`,                        'kWh/mês'],
    ['Economia Est. Mensal:',  brl(ecoMedia),                       ''],
  ].forEach(([l, v, u]) => {
    y4 = drawRow(p4, [
      {x:ML,      w:rL, fill:C.cinzaChumbo, text:l, fn:fontBold, cl:C.cinza,  bc:C.preto},
      {x:ML+rL,   w:rV, fill:C.branco,      text:v, fn:fontBold, cl:C.preto,  bc:C.preto, al:'r'},
      {x:ML+rL+rV,w:rU, fill:C.branco,      text:u, fn:fontReg,  cl:C.cinza,  bc:C.preto},
    ], y4);
  });
  y4 -= 12; // espaço extra antes do próximo header

  // Tabela capacidade mensal — cabeçalho laranja
  y4 = drawSecHeader(p4, 'Detalhamento da sua Capacidade de Bombeamento:', y4, C.laranja);
  y4 -= 4;

  const b1=CW*0.25; const b2=CW*0.25; const b3=CW*0.25; const b4=CW*0.25;
  y4 = drawRow(p4, [
    {x:ML,          w:b1, fill:C.cinzaChumbo, text:'Mês',                   fn:fontBold, cl:C.preto, bc:C.preto, al:'c'},
    {x:ML+b1,       w:b2, fill:C.cinzaChumbo, text:'HSP (h/dia)',           fn:fontBold, cl:C.preto, bc:C.preto, al:'c'},
    {x:ML+b1+b2,    w:b3, fill:C.cinzaChumbo, text:'Vazão Diária (m³/dia)', fn:fontBold, cl:C.preto, bc:C.preto, al:'c'},
    {x:ML+b1+b2+b3, w:b4, fill:C.cinzaChumbo, text:'Vazão Horária (m³/h)', fn:fontBold, cl:C.preto, bc:C.preto, al:'c'},
  ], y4);
  meses.forEach((m, i) => {
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    y4 = drawRow(p4, [
      {x:ML,          w:b1, fill, text:m.mes,                         fn:fontBold, cl:C.preto,   bc:C.preto, al:'c'},
      {x:ML+b1,       w:b2, fill, text:(m.hsp||0).toFixed(2),                      bc:C.preto,  al:'c'},
      {x:ML+b1+b2,    w:b3, fill, text:(m.vazaoDiaria||0).toFixed(2),              bc:C.preto,  al:'c'},
      {x:ML+b1+b2+b3, w:b4, fill, text:(m.vazaoHoraria||0).toFixed(2),fn:fontBold, cl:C.laranja,bc:C.preto, al:'c'},
    ], y4);
  });
  y4 -= 10;

  // Gráfico vazão — ocupa espaço restante com eixos
  drawText(p4, 'Projeção de Vazão Média Horária (m³/h)',
    cx('Projeção de Vazão Média Horária (m³/h)', 9, fontBold), y4, 9, fontBold, C.preto);
  y4 -= 10;

  const chartData4 = meses.map((m, i) => ({ label: MESES[i], v: m.vazaoHoraria||0 }));
  const availH4 = y4 - 30; // espaço disponível até o final da página
  drawBarChart(p4, chartData4, y4, availH4, {
    colors: [C.laranja],
    labelKey: 'label',
    valueKeys: ['v'],
    noteTxt: '* A estimativa de vazão média apresentada foi calculada em um cenário ideal. Por se tratar de uma fonte intermitente, a geração pode sofrer variações em decorrência de sombreamento e/ou posicionamento dos módulos.',
    ySteps: 5,
  });
  y4 = 28;

  // ============================================================
  // PG 5 — DETALHAMENTO DOS PRODUTOS
  // ============================================================
  const p5 = pdfDoc.addPage([W, H]);
  let y5 = H - 30;

  y5 = drawSecHeader(p5, '3| Detalhamento dos Produtos', y5);
  y5 -= 6;

  y5 = drawParagraph(p5, 'A seguir, detalharemos as informações referentes aos equipamentos deste orçamento. Informamos que não trabalhamos com estoque físico, então a disponibilidade dos equipamentos pode sofrer alterações. Neste caso, eventuais alterações em potência e modelo dos equipamentos orçados serão previamente comunicadas.', ML, y5, CW, 8, fontReg, C.preto, 11);
  y5 -= 8;

  // Módulo + Inversor lado a lado
  const hW = CW/2 - 4;
  const invX = ML + hW + 8;

  // Headers
  drawRect(p5, ML,   y5-4, hW, 14, C.laranja); drawText(p5,'Módulo Fotovoltaico',    ML+4,   y5, 9, fontBold, C.branco);
  drawRect(p5, invX, y5-4, hW, 14, C.laranja); drawText(p5,'Inversor de Frequência', invX+4, y5, 9, fontBold, C.branco);
  y5 -= 18;

  const mW1=hW*0.55; const mW2=hW*0.45;
  const yModStart = y5;
  [
    ['Potência:',           `${proposta.kit?.modulo?.potencia||'–'} Wp`],
    ['Modelo:',              proposta.kit?.modulo?.modelo||'–'],
    ['Fabricante:',          proposta.kit?.modulo?.marca||'–'],
    ['Garantia (Geração):',  `${proposta.kit?.modulo?.garantiageracao||'–'} Anos`],
    ['Garantia (Fábrica):',  `${proposta.kit?.modulo?.garantiafabrica||'–'} Anos`],
  ].forEach(([l, v]) => {
    y5 = drawRow(p5, [
      {x:ML,      w:mW1, fill:C.cinzaChumbo, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:ML+mW1,  w:mW2, fill:C.branco,      text:v, fn:fontBold, cl:C.preto, bc:C.preto},
    ], y5);
  });

  let yInv = yModStart;
  [
    ['Potência:',   `${proposta.kit?.inversor?.potencia||'–'} CV`],
    ['Modelo:',      proposta.kit?.inversor?.modelo||'–'],
    ['Fabricante:',  proposta.kit?.inversor?.fabricante||'WEG'],
    ['Garantia:',   `${proposta.kit?.inversor?.garantia||'–'} Anos`],
    ['Tensão:',     `${proposta.kit?.inversor?.tensao||'–'} V`],
  ].forEach(([l, v]) => {
    yInv = drawRow(p5, [
      {x:invX,      w:mW1, fill:C.cinzaChumbo, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:invX+mW1,  w:mW2, fill:C.branco,      text:v, fn:fontBold, cl:C.preto, bc:C.preto},
    ], yInv);
  });

  // Imagens dos equipamentos
  const imgBottom = Math.min(y5, yInv) - 8;
  if (moduloImg) {
    const sc = moduloImg.scaleToFit(hW*0.55, 110);
    p5.drawImage(moduloImg, { x:ML+(hW-sc.width)/2, y:imgBottom-sc.height, width:sc.width, height:sc.height });
  }
  if (inversorImg) {
    const sc = inversorImg.scaleToFit(hW*0.55, 110);
    p5.drawImage(inversorImg, { x:invX+(hW-sc.width)/2, y:imgBottom-sc.height, width:sc.width, height:sc.height });
  }

  drawText(p5, '*imagens meramente ilustrativas', ML, imgBottom-120, 7, fontReg, C.cinza);

  // Estrutura
  let yEst = imgBottom - 130;
  drawRect(p5, ML, yEst-4, CW, 14, C.laranja);
  drawText(p5, 'Estrutura de Fixação', ML+4, yEst, 9, fontBold, C.branco);
  yEst -= 18;

  [
    ['Superfície',            'SOLO'],
    ['Garantia:',             '15 anos'],
    ['Material:',             'Aço INOX'],
    ['Peso Total Estimado:',  `${pesoTot} kg`],
  ].forEach(([l, v]) => {
    yEst = drawRow(p5, [
      {x:ML,       w:CW*0.4, fill:C.cinzaChumbo, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:ML+CW*0.4,w:CW*0.6, fill:C.branco,      text:v, fn:fontBold, cl:C.preto, bc:C.preto},
    ], yEst);
  });

  if (estruturaImg) {
    const sc = estruturaImg.scaleToFit(200, 110);
    p5.drawImage(estruturaImg, { x:(W-sc.width)/2, y:yEst-sc.height-8, width:sc.width, height:sc.height });
  }

  // ============================================================
  // PG 6 — ANÁLISE FINANCEIRA (gráficos grandes)
  // ============================================================
  const p6 = pdfDoc.addPage([W, H]);
  let y6 = H - 30;

  y6 = drawSecHeader(p6, '4 | Análise Financeira', y6);
  y6 -= 10;

  // Gráfico 1 — Consumo × Economia (ocupa ~45% da página)
  drawText(p6, 'Consumo Mensal × Economia Mensal',
    cx('Consumo Mensal × Economia Mensal', 10, fontBold), y6, 10, fontBold, C.preto);
  y6 -= 16;

  // Gráfico 1 — Consumo × Economia com eixos (44% da altura disponível)
  const chartData6a = meses.map((m, i) => ({
    label: MESES[i],
    c: m.energiaMes||0,
    e: m.economiaMes||0,
  }));
  const availH6a = Math.round((y6 - 60) * 0.44);
  drawBarChart(p6, chartData6a, y6, availH6a, {
    colors: [C.laranja, C.verde],
    labelKey: 'label',
    valueKeys: ['c', 'e'],
    legends: ['Consumo (kWh)', 'Economia (R$)'],
    ySteps: 5,
  });
  y6 -= availH6a + 16;

  // Gráfico 2 — Vazão Mensal (m³/mês) com eixos
  drawText(p6, 'Projeção de Vazão Média Mensal (m³/mês)',
    cx('Projeção de Vazão Média Mensal (m³/mês)', 10, fontBold), y6, 10, fontBold, C.preto);
  y6 -= 10;

  const chartData6b = meses.map((m, i) => ({
    label: MESES[i],
    v: m.vazaoMensal||0,
  }));
  const availH6b = y6 - 28;
  drawBarChart(p6, chartData6b, y6, availH6b, {
    colors: [C.laranja],
    labelKey: 'label',
    valueKeys: ['v'],
    noteTxt: '* Estimativa baseada em dados solarimétricos. Valores reais podem variar conforme condições locais.',
    ySteps: 5,
  });
  y6 = 28;

  // ============================================================
  // PG 7 — RESUMO E VALOR DO INVESTIMENTO
  // ============================================================
  const p7 = pdfDoc.addPage([W, H]);
  let y7 = H - 30;

  y7 = drawSecHeader(p7, '8 | Resumo e valor do investimento', y7);
  y7 -= 8;

  // ── Bloco 1: Indicadores financeiros + Simulação lado a lado ──
  const halfC = CW / 2 - 4;

  // Coluna esquerda — indicadores
  drawRect(p7, ML, y7-4, halfC, 14, C.laranja);
  drawText(p7, 'Retorno do Investimento', ML+4, y7, 9, fontBold, C.branco);
  let yLeft = y7 - 18;
  const invX7 = ML + halfC + 8;

  [
    ['Payback Recurso Próprio:', `${payback} anos`],
    ['Economia Mensal Est.:',    brl(ecoMedia)],
    ['Economia Anual Est.:',     brl(ecoAnual)],
    ['Valor do Investimento:',   brl(valorProjeto)],
    ['Prazo de Entrega:',        '90 dias (confirmação + assinatura)'],
    ['Validade da Proposta:',    '10 dias'],
  ].forEach(([l, v], i) => {
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    yLeft = drawRow(p7, [
      {x:ML,           w:halfC*0.6, fill, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:ML+halfC*0.6, w:halfC*0.4, fill, text:v, fn:fontBold, cl:C.preto, bc:C.preto, al:'r'},
    ], yLeft);
  });

  // Coluna direita — simulação de financiamento
  drawRect(p7, invX7, y7-4, halfC, 14, C.laranja);
  drawText(p7, 'Simulação de Financiamento', invX7+4, y7, 9, fontBold, C.branco);
  let yRight = y7 - 18;

  drawBRect(p7, invX7, yRight-12, halfC, 12, C.azulClaro, C.bordaAzul);
  drawText(p7, 'Prazo de Carência Sugerido: 90 dias corridos', invX7+4, yRight-8, 7, fontReg, C.preto);
  yRight -= 14;

  [
    ['Simulação em 36x', brl(calcParcela(valorProjeto, 36))],
    ['Simulação em 48x', brl(calcParcela(valorProjeto, 48))],
    ['Simulação em 60x', brl(calcParcela(valorProjeto, 60))],
    ['Simulação em 72x', brl(calcParcela(valorProjeto, 72))],
  ].forEach(([l, v], i) => {
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    yRight = drawRow(p7, [
      {x:invX7,           w:halfC*0.6, fill, text:l, fn:fontBold, cl:C.cinza,   bc:C.preto},
      {x:invX7+halfC*0.6, w:halfC*0.4, fill, text:v, fn:fontBold, cl:C.laranja, bc:C.preto, al:'r'},
    ], yRight);
  });

  drawBRect(p7, invX7, yRight-20, halfC, 18, C.cinzaClaro, C.bordaAzul);
  drawText(p7, '* Taxa de juros 1,85% a.m. IOF e Tarifa de cadastro inclusos.', invX7+3, yRight-10, 6, fontReg, C.cinza);
  drawText(p7, '** Sujeito à aprovação de crédito.', invX7+3, yRight-18, 6, fontReg, C.cinza);

  // Espaço garantido entre simulação e tabela
  y7 = Math.min(yLeft, yRight) - 24;

  // ── Bloco 2: Resumo de produtos ──────────────────────────────
  y7 = drawSecHeader(p7, 'Resumo dos Componentes', y7, C.laranja);
  y7 -= 4;

  const cp1=CW*0.7; const cp2=CW*0.3;
  y7 = drawRow(p7, [
    {x:ML,    w:cp1, fill:C.cinzaChumbo, text:'Componentes', fn:fontBold, cl:C.preto, bc:C.preto},
    {x:ML+cp1,w:cp2, fill:C.cinzaChumbo, text:'Quantidade',  fn:fontBold, cl:C.preto, bc:C.preto, al:'c'},
  ], y7);
  [
    ['Módulo Monocristalino:', `${qtdMod}`],
    ['Inversor de Frequência:', `${proposta.kit?.quantidadeinversores||1}`],
    ['Estrutura para Solo:', `${Math.ceil(qtdMod/4)}`],
    ['Kit Material Elétrico:', '1'],
    ['Frete:', 'Incluso'],
    ['Serviço de Instalação e Projetos:', '1'],
    ['Acompanhamento Remoto:', 'Incluso'],
  ].forEach(([l, v], i) => {
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    y7 = drawRow(p7, [
      {x:ML,    w:cp1, fill, text:l, fn:fontBold, cl:C.cinza, bc:C.preto},
      {x:ML+cp1,w:cp2, fill, text:v, fn:fontBold, cl:C.preto, bc:C.preto, al:'c'},
    ], y7);
  });
  y7 -= 14; // espaço antes do valor total

  // ── Bloco 3: Valor total ────────────────────────────────────
  drawRect(p7, ML, y7-4, CW, 18, C.laranja);
  drawText(p7, `Valor total do Projeto: ${brl(valorProjeto)}`, ML+6, y7, 12, fontBold, C.branco);
  y7 -= 40;

  // ── Rodapé — mesmo padrão on-grid ────────────────────────────
  drawLine(p7, ML,           y7+10, ML+CW*0.44, y7+10, C.preto, 0.8);
  drawLine(p7, ML+CW*0.56,   y7+10, ML+CW,      y7+10, C.preto, 0.8);
  drawText(p7, empresa?.razaoSocial || 'Marcki Engenharia LTDA', ML, y7,    8, fontBold, C.preto);
  drawText(p7, `CNPJ: ${empresa?.cnpj || '38.143.553/0001-76'}`, ML, y7-11, 8, fontReg,  C.preto);
  drawText(p7, `Cliente: ${proposta.cliente?.nome||''}`,    ML+CW*0.56, y7,    8, fontReg, C.preto);
  drawText(p7, `CPF/CNPJ: ${proposta.cliente?.cpf||''}`,   ML+CW*0.56, y7-11, 8, fontReg, C.preto);

  const rodapeFinal = `${empresa?.nome || 'MARCKI Engenharia'} — ${empresa?.enderecoRodape || 'Araguari/MG'}`;
  drawText(p7, rodapeFinal, cx(rodapeFinal, 8, fontReg), 20, 8, fontReg, C.cinza);

  // ── Export ────────────────────────────────────────────────────
  const pdfBytes = await pdfDoc.save();
  const blob  = new Blob([pdfBytes], { type: 'application/pdf' });
  const url   = URL.createObjectURL(blob);
  const link  = document.createElement('a');
  link.href   = url;
  link.download = `Proposta_Bombeamento_${(proposta.cliente?.nome||'Cliente').replace(/ /g,'_')}_Marcki.pdf`;
  link.click();
  URL.revokeObjectURL(url);
};

export default generatePdfBombeamento;